import React, { useEffect, useState } from 'react';
import { CaretDown, CaretUp, FileText, Warning } from '@phosphor-icons/react';
import TaskDistributionCollapsibleSection from './TaskDistributionCollapsibleSection.jsx';
import { getRouteColor } from '../../../styles/routeColors.js';
import {
  countTaskStops,
  formatDeviationLabel,
  formatDistanceKm,
  formatMinutes,
  getOptimizationCriterion,
  getStopAssignmentLabel
} from '../utils/taskDistributionUtils.jsx';

const TaskDistributionResultsSection = ({
  distributionResult,
  resultMode = 'preview',
  utilizedAssignments,
  selectedTeamCount,
  idleAssignments,
  taskById = new Map()
}) => {
  const [expandedAssignmentIds, setExpandedAssignmentIds] = useState([]);
  const [isIdleAssignmentsExpanded, setIsIdleAssignmentsExpanded] = useState(true);
  const [isUnassignedTasksExpanded, setIsUnassignedTasksExpanded] = useState(true);
  const [isWarningsExpanded, setIsWarningsExpanded] = useState(false);
  const [expandedTaskDescriptionId, setExpandedTaskDescriptionId] = useState(null);

  useEffect(() => {
    if (!distributionResult) {
      setExpandedAssignmentIds([]);
      setIsIdleAssignmentsExpanded(true);
      setIsUnassignedTasksExpanded(true);
      setIsWarningsExpanded(false);
      setExpandedTaskDescriptionId(null);
      return;
    }

    setExpandedAssignmentIds(
      (distributionResult.assignments || []).map((assignment) => String(assignment.teamId))
    );
    setIsIdleAssignmentsExpanded(true);
    setIsUnassignedTasksExpanded(true);
    setIsWarningsExpanded(false);
    setExpandedTaskDescriptionId(null);
  }, [distributionResult]);

  if (!distributionResult) {
    return null;
  }

  const isConfirmedMode = resultMode === 'confirmed';
  const isOverlayMode = resultMode === 'overlay';
  const appliedOptimizationCriterion = distributionResult.metadata?.appliedConfiguration?.optimizationCriterion;
  const appliedOptimizationCriterionLabel = appliedOptimizationCriterion
    ? getOptimizationCriterion(appliedOptimizationCriterion).label
    : null;
  const description = isConfirmedMode
    ? `${distributionResult.metadata?.totalAssigned || 0} tareas dentro de los recorridos confirmados`
    : isOverlayMode
      ? 'Mostrando la previsualizacion actual junto con el resto de recorridos confirmados del dia.'
      : `${distributionResult.metadata?.totalAssigned || 0} de ${distributionResult.metadata?.totalTasks || 0} tareas asignadas`;
  const badge = isConfirmedMode
    ? (
      <div className="rounded-full border border-amber/30 bg-amber-hover/10 px-3 py-1 text-xs font-semibold text-accent-text">
        Recorridos confirmados
      </div>
    )
    : isOverlayMode
      ? (
        <div className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-100">
          Recorridos visibles: {(distributionResult.assignments || []).length}
        </div>
      )
      : (
        <div className="rounded-full border border-line bg-surface-2 px-3 py-1 text-xs font-semibold text-content">
          Equipos con tareas: {utilizedAssignments.length}/{selectedTeamCount}
        </div>
      );

  const toggleAssignment = (teamId) => {
    const normalizedTeamId = String(teamId);
    setExpandedAssignmentIds((current) => (
      current.includes(normalizedTeamId)
        ? current.filter((currentTeamId) => currentTeamId !== normalizedTeamId)
        : [...current, normalizedTeamId]
    ));
  };

  return (
    <TaskDistributionCollapsibleSection
      title="Resultados"
      description={description}
      badge={badge}
    >
      {appliedOptimizationCriterionLabel && (
        <div className="mb-4 rounded-2xl border border-line bg-surface-2 px-4 py-3 text-sm text-content-muted">
          Criterio de optimización aplicado: <span className="font-semibold text-content">{appliedOptimizationCriterionLabel}</span>
        </div>
      )}

      {(distributionResult.warnings || []).length > 0 && (
        <div className="mb-4 overflow-hidden rounded-2xl border border-warning/40 bg-warning/10 text-sm text-warning">
          <button
            type="button"
            onClick={() => setIsWarningsExpanded((current) => !current)}
            aria-expanded={isWarningsExpanded}
            className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition hover:bg-warning/10"
          >
            <div className="flex min-w-0 items-center gap-3">
              <Warning size={16} className="shrink-0" />
              <div>
                <div className="font-semibold">Advertencias ({distributionResult.warnings.length})</div>
                <p className="mt-1 text-xs text-warning/80">
                  Posibles conflictos detectados durante el calculo.
                </p>
              </div>
            </div>
            <span className="shrink-0 text-warning">
              {isWarningsExpanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
            </span>
          </button>

          {isWarningsExpanded && (
            <div className="border-t border-warning/20 px-4 py-4">
              <div className="space-y-2">
                {(distributionResult.warnings || []).map((warningMessage, index) => (
                  <div key={`${warningMessage}-${index}`} className="flex gap-2">
                    <Warning size={16} className="mt-0.5 shrink-0" />
                    <span>{warningMessage}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {(distributionResult.assignments || []).map((assignment, index) => {
          const taskStopCount = countTaskStops(assignment);
          const normalizedTeamId = String(assignment.teamId);
          const isExpanded = expandedAssignmentIds.includes(normalizedTeamId);

          return (
            <article
              key={assignment.teamId}
              className="overflow-hidden rounded-2xl border border-line bg-surface-2/70"
            >
              <button
                type="button"
                onClick={() => toggleAssignment(assignment.teamId)}
                aria-expanded={isExpanded}
                className="flex w-full flex-col gap-3 px-4 py-4 text-left transition hover:bg-surface/40 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="h-4 w-4 rounded-full border border-white/40"
                    style={{ backgroundColor: assignment.color || getRouteColor(index) }}
                  />
                  <div>
                    <div className="text-base font-semibold text-content">{assignment.teamName}</div>
                    <div className="text-xs text-content-muted">
                      {taskStopCount} tareas - {formatMinutes(assignment.totalTimeMinutes)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 md:justify-end">
                  <div className="text-left text-xs text-content-muted md:text-right">
                    <div>Viaje: {formatMinutes(assignment.travelTimeMinutes)}</div>
                    <div>Distancia: {formatDistanceKm(assignment.distanceKm)}</div>
                    <div>Trabajo: {formatMinutes(assignment.workTimeMinutes)}</div>
                    {Number(assignment.waitTimeMinutes || 0) > 0 && (
                      <div>Espera: {formatMinutes(assignment.waitTimeMinutes)}</div>
                    )}
                  </div>
                  <span className="shrink-0 text-content-muted">
                    {isExpanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-line px-4 py-4">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(assignment.usageByCategory || {}).map(([category, usage]) => (
                      <span
                        key={`${assignment.teamId}-${category}`}
                        className="rounded-full border border-line bg-surface px-3 py-1 text-xs font-medium text-content"
                      >
                        {usage.label}: {usage.used}/{usage.limit} min
                      </span>
                    ))}
                  </div>

                  {assignment.punctualitySummary?.scheduledTaskCount > 0 && (
                    <div className="mt-4 rounded-2xl border border-line bg-surface-2 px-4 py-3 text-sm text-content-muted">
                      Puntualidad: {assignment.punctualitySummary.onTimeTaskCount}/{assignment.punctualitySummary.scheduledTaskCount} en ventana - desv. prom. {assignment.punctualitySummary.averageDeviationMinutes} min
                    </div>
                  )}

                  <div className="mt-4 space-y-2">
                    {(assignment.stops || []).map((stop) => {
                      const isBase = stop.type === 'BASE_START' || stop.type === 'BASE_END';
                      const isOutOfWindow = stop.withinWindow === false;
                      const sourceTask = taskById.get(String(stop.taskId || ''));
                      const assignmentLabel = getStopAssignmentLabel(stop, sourceTask);
                      const taskDescriptionId = `task-description-${assignment.teamId}-${stop.order}`;
                      const isTaskDescriptionExpanded = expandedTaskDescriptionId === taskDescriptionId;
                      const taskDescription = sourceTask?.description?.trim() || stop.description?.trim();
                      return (
                        <div
                          key={`${assignment.teamId}-${stop.order}-${stop.type}`}
                          className={`rounded-xl border px-3 py-3 text-sm ${
                            isOutOfWindow
                              ? 'border-warning/30 bg-warning/10'
                              : 'border-line bg-surface/60'
                          }`}
                        >
                          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 text-content">
                                <span className="font-semibold">{stop.estimatedArrivalTime || '--:--'}</span>
                                <span>{isBase ? stop.name : stop.title}</span>
                                {!isBase && (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedTaskDescriptionId((current) => (
                                      current === taskDescriptionId ? null : taskDescriptionId
                                    ))}
                                    aria-label={`Ver descripción de ${stop.title || 'la tarea'}`}
                                    aria-expanded={isTaskDescriptionExpanded}
                                    aria-controls={taskDescriptionId}
                                    title="Ver descripción de la tarea"
                                    className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-amber/70 ${
                                      isTaskDescriptionExpanded
                                        ? 'border-amber bg-amber-hover/20 text-accent-text'
                                        : 'border-line bg-surface-2 text-content-muted hover:border-amber hover:text-accent-text'
                                    }`}
                                  >
                                    <FileText size={14} weight="bold" />
                                  </button>
                                )}
                                {assignmentLabel && !isBase && (
                                  <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[11px] font-semibold text-amber-100">
                                    {assignmentLabel}
                                  </span>
                                )}
                              </div>

                              {!isBase && (
                                <div className="mt-1 text-xs text-content-muted">
                                  {stop.address || 'Sin direccion'} - {stop.category || 'Sin categoria'}
                                </div>
                              )}
                            </div>

                            {!isBase && (
                              <div className={`text-xs ${isOutOfWindow ? 'text-warning' : 'text-content-muted'}`}>
                                {stop.scheduledTime
                                  ? `Prog. ${stop.scheduledTime} - ${formatDeviationLabel(stop.deviationMinutes)}`
                                  : 'Sin horario programado'}
                              </div>
                            )}
                          </div>

                          {!isBase && isTaskDescriptionExpanded && (
                            <div
                              id={taskDescriptionId}
                              role="tooltip"
                              className="mt-2 w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs leading-5 text-content shadow-lg"
                            >
                              <span className="font-semibold text-content">Descripción:</span>{' '}
                              <span className="whitespace-pre-wrap">
                                {taskDescription || 'La tarea no tiene una descripción cargada.'}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {idleAssignments.length > 0 && (
        <div className="mt-5 overflow-hidden rounded-2xl border border-line bg-surface-2/60">
          <button
            type="button"
            onClick={() => setIsIdleAssignmentsExpanded((current) => !current)}
            aria-expanded={isIdleAssignmentsExpanded}
            className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition hover:bg-surface/40"
          >
            <div>
              <div className="text-sm font-semibold text-content">Equipos sin tareas en esta ejecución</div>
              <p className="mt-1 text-xs text-content-muted">
                Se enviaron al calculo, pero la propuesta devuelta no les asigno paradas.
              </p>
            </div>
            <span className="shrink-0 text-content-muted">
              {isIdleAssignmentsExpanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
            </span>
          </button>

          {isIdleAssignmentsExpanded && (
            <div className="border-t border-line px-4 py-4">
              <div className="flex flex-wrap gap-2">
                {idleAssignments.map((assignment) => (
                  <span
                    key={`idle-${assignment.teamId}`}
                    className="rounded-full border border-line bg-surface px-3 py-1 text-xs font-medium text-content"
                  >
                    {assignment.teamName}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {(distributionResult.unassignedTasks || []).length > 0 && (
        <div className="mt-5 overflow-hidden rounded-2xl border border-rose-500/30 bg-rose-500/8">
          <button
            type="button"
            onClick={() => setIsUnassignedTasksExpanded((current) => !current)}
            aria-expanded={isUnassignedTasksExpanded}
            className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition hover:bg-rose-500/10"
          >
            <div className="text-sm font-semibold text-rose-100">
              Tareas sin asignar ({distributionResult.unassignedTasks.length})
            </div>
            <span className="shrink-0 text-rose-100">
              {isUnassignedTasksExpanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
            </span>
          </button>

          {isUnassignedTasksExpanded && (
            <div className="border-t border-rose-500/20 px-4 py-4">
              <div className="space-y-2">
                {(distributionResult.unassignedTasks || []).map((task) => (
                  <div
                    key={task.taskId}
                    className="rounded-xl border border-rose-500/20 bg-surface-2/70 px-3 py-3 text-sm text-content"
                  >
                    <div className="font-medium text-content">{task.title}</div>
                    <div className="mt-1 text-xs text-content-muted">
                      {task.category || 'Sin categoria'} - {task.reason}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </TaskDistributionCollapsibleSection>
  );
};

export default TaskDistributionResultsSection;
